import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    FormEventHandler,
    ReactElement
} from 'react';
import {
    UIButton,
    UIFormInput,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIMultiSelectInput,
    UIRadioInput
} from '@/ui';
import { ResolveTaskRequest } from '@/api';
import { useResolveTaskAction } from '../actions';
import { useTasksTranslation } from '../hooks';
import {
    EMERGENCY_SERVICES,
    EmergencyService,
    INJURY_OPTIONS,
    Injury,
    RESOLVE_REASONS,
    ResolveReason,
    getEmergencyServiceLabel,
    getInjuryLabel,
    getResolveReasonLabel
} from '../util/resolve-vocabulary';

const HEIGHT = 32;

type ResolveAlertButtonProps = {
    taskId: string;
};

/**
 * The button that opens the modal below, and the only way into it.
 */
export function ResolveAlertButton({
    taskId
}: ResolveAlertButtonProps): ReactElement {
    const { t } = useTasksTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    function onResolveClick() {
        setIsModalOpen(true);
    }
    return (
        <>
            <UIButton
                variant="primary"
                onClick={onResolveClick}
                customHeight={HEIGHT}
                leftIcon="check-circle"
            >
                {t('components.resolve_alert_modal.button_label')}
            </UIButton>
            <ResolveAlertModal
                taskId={taskId}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </>
    );
}

type ResolveAlertModalProps = {
    taskId: string;
    isOpen: boolean;
    onClose: () => void;
};

export function ResolveAlertModal(props: ResolveAlertModalProps): ReactElement {
    const { taskId, isOpen, onClose } = props;

    const { resolve, loading } = useResolveTaskAction({ taskId });

    function handleClose() {
        onClose();
    }

    function onSubmit(data: ResolveTaskRequest) {
        resolve(data)
            .catch(() => {}) // Error handled in the action
            .finally(() => {
                handleClose();
            });
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            <ResolveAlertModalForm
                isOpen={isOpen}
                onClose={handleClose}
                loading={loading}
                onSubmit={onSubmit}
            />
        </UIModal>
    );
}

type ResolveAlertModalFormProps = {
    isOpen: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: ResolveTaskRequest) => void;
};

function ResolveAlertModalForm(
    props: ResolveAlertModalFormProps
): ReactElement {
    const { isOpen } = props;

    if (isOpen) {
        return <FormContent {...props} />;
    } else {
        return <></>;
    }
}

type IsFalseAlarm = 'YES' | 'NO';
type IsFalseAlarmOption = { value: IsFalseAlarm; label: string };
type ReasonOption = { value: ResolveReason; label: string };
type InjuryOption = { value: Injury; label: string };
type EmergencyServiceOption = { value: EmergencyService; label: string };

function FormContent(props: ResolveAlertModalFormProps): ReactElement {
    const { loading, onClose, onSubmit } = props;
    const { t } = useTasksTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Is false alarm
     */
    const [isFalseAlarm, setIsFalseAlarm] = useState<IsFalseAlarm>('YES');
    const isFalseAlarmOptions: IsFalseAlarmOption[] = useMemo(
        () => [
            { value: 'YES', label: t('shared.labels.yes') },
            { value: 'NO', label: t('shared.labels.no') }
        ],
        [t]
    );
    const selectedFalseAlarm = useMemo(
        () =>
            isFalseAlarmOptions.find(
                (option) => option.value === isFalseAlarm
            ) ?? isFalseAlarmOptions[0],
        [isFalseAlarmOptions, isFalseAlarm]
    );

    /**
     * Reasons. The labels come from `resolve-vocabulary`, which is also what
     * `TaskEventsList` reads them back with — one set of words, one place.
     */
    const [reasons, setReasons] = useState<ReasonOption[]>([]);
    const [reasonsError, setReasonsError] = useState('');
    const reasonsOptions: ReasonOption[] = useMemo(
        () =>
            RESOLVE_REASONS.map((value) => ({
                value,
                label: getResolveReasonLabel(t, value)
            })),
        [t]
    );
    const checkReasons = useCallback(
        (reasons: ReasonOption[]): string => {
            if (reasons.length === 0) {
                return t('shared.fields.resolve_reasons.error_required');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setReasonsError(checkReasons(reasons));
        }
    }, [reasons, showRealTimeErrors, checkReasons]);

    /**
     * Injury
     */
    const [injury, setInjury] = useState<Injury>('NONE');
    const injuryOptions: InjuryOption[] = useMemo(
        () =>
            INJURY_OPTIONS.map((value) => ({
                value,
                label: getInjuryLabel(t, value)
            })),
        [t]
    );
    const selectedInjury = useMemo(
        () => injuryOptions.find((option) => option.value === injury),
        [injuryOptions, injury]
    );

    /**
     * Emergency services
     */
    const [emergencyServices, setEmergencyServices] = useState<
        EmergencyServiceOption[]
    >([]);
    const emergencyServiceOptions: EmergencyServiceOption[] = useMemo(
        () =>
            EMERGENCY_SERVICES.map((value) => ({
                value,
                label: getEmergencyServiceLabel(t, value)
            })),
        [t]
    );

    /**
     * Note
     */
    const noteDefault = '';
    const [note, setNote] = useState(noteDefault);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        if (isFalseAlarm === 'YES') {
            onSubmit?.({ isFalseAlarm: true });
            return;
        }

        setShowRealTimeErrors(true);

        if (!checkReasons(reasons)) {
            setShowRealTimeErrors(false);
            const sanitisedNote = note.trim();
            onSubmit?.({
                isFalseAlarm: false,
                reasons: reasons.map((reason) => reason.value),
                injury,
                emergencyServices: emergencyServices.map(
                    (service) => service.value
                ),
                note: sanitisedNote.length > 0 ? sanitisedNote : undefined
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <UIModalHeader
                    title={t('components.resolve_alert_modal.title')}
                    subtitle={t('components.resolve_alert_modal.subtitle')}
                />
                <UIModalContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <UIRadioInput
                            label={t(
                                'components.resolve_alert_modal.false_alarm_label'
                            )}
                            options={isFalseAlarmOptions}
                            selected={selectedFalseAlarm}
                            required
                            onChange={(option) => {
                                setIsFalseAlarm(option.value as IsFalseAlarm);
                            }}
                        />

                        {isFalseAlarm === 'NO' && (
                            <>
                                <UIMultiSelectInput
                                    label={t(
                                        'components.resolve_alert_modal.reasons_label'
                                    )}
                                    placeholder={t('shared.labels.select')}
                                    required
                                    options={reasonsOptions}
                                    value={reasons}
                                    onChange={(selectedOptions) => {
                                        setReasons(
                                            selectedOptions as ReasonOption[]
                                        );
                                    }}
                                    errorText={reasonsError}
                                />

                                <UIMultiSelectInput
                                    label={t(
                                        'components.resolve_alert_modal.emergency_services_label'
                                    )}
                                    placeholder={t(
                                        'components.resolve_alert_modal.emergency_services_placeholder'
                                    )}
                                    options={emergencyServiceOptions}
                                    value={emergencyServices}
                                    onChange={(selectedOptions) => {
                                        setEmergencyServices(
                                            selectedOptions as EmergencyServiceOption[]
                                        );
                                    }}
                                />

                                <UIRadioInput
                                    label={t(
                                        'components.resolve_alert_modal.injury_label'
                                    )}
                                    required
                                    options={injuryOptions}
                                    selected={selectedInjury}
                                    onChange={(option) => {
                                        setInjury(option.value as Injury);
                                    }}
                                />

                                <UIFormInput
                                    name="note"
                                    label={t('shared.fields.note.label')}
                                    placeholder={t(
                                        'components.resolve_alert_modal.note_placeholder'
                                    )}
                                    value={note}
                                    type="text"
                                    autoComplete="on"
                                    maxLength={1000}
                                    onChange={setNote}
                                />
                            </>
                        )}
                    </div>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: t('shared.labels.submit')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            disabled: loading,
                            onClick: onClose
                        }
                    ]}
                />
            </form>
        </>
    );
}
